import React from 'react';
import { CrossmintPaymentElement } from '@crossmint/client-sdk-react-ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatEther } from 'viem';
import { faQuestionCircle } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { getChainName } from '@soundxyz/utils/constants/constants';
import {
  getContractsForChain,
  getDeprecateMintersForChain,
} from '@soundxyz/utils/constants/contract-addresses';
import { StandardModal } from '@components/main-components/modals/StandardModal';
import { Tooltip } from '@components/main-components/tooltip/Tooltip';
import { CrossMintProcessingModal } from '@components/modals/CrossMintProcessingModal';
import { useAuth } from '@context/auth/base';
import { MediaQuery } from '@context/mediaQuery';
import { ModalContext, ModalType } from '@context/modal';
import type { FragmentType } from '@graphql/reactQuery';
import { BuyEditionSongDetailsFragmentDoc, getFragment } from '@graphql/reactQuery';
import { useEthToUsdValue } from '@hooks/useEthToUsd';
import { useLogError } from '@hooks/useLogErrorNew';
import {
  CROSSMINT_CC_ATTRIBUTION_ID,
  CROSSMINT_ENVIRONMENT,
  getCrossMintContractClientIds,
} from '@utils/constants';
import { formatWeiToEthForDisplay } from '@utils/helpers';
import { SongDetails } from '../CreditCardCheckoutSongDetails';
import { ALLOWED_INTERFACES_FOR_CC, CREDIT_CARD_FEE_INFO_COPY } from '../credit-card-helpers';

export const CrossMintEditionCreditCardModal = ({
  quantity,
  totalPriceEth,
  data,
  minterAddress,
  affiliate,
  contractInterFaceId,
  mintId,
  contractAddress,
  onSuccessfulTransaction,
  proof,
}: {
  quantity: number;
  totalPriceEth: string;
  minterAddress: string;
  affiliate: string;
  contractInterFaceId: string;
  data: FragmentType<BuyEditionSongDetailsFragmentDoc>;
  onSuccessfulTransaction: () => void;
  mintId: number;
  contractAddress: string;
  proof: string[] | null;
}) => {
  const { authUserAddress } = useAuth();
  const { closeCancelModal, openModal } = ModalContext.useContainer();
  const logError = useLogError();
  const [breakdown, setBreakdown] = React.useState<{ total: number; fee: number }>();
  const { tabletLandscape } = MediaQuery.useContainer();
  const release = getFragment(BuyEditionSongDetailsFragmentDoc, data);
  const { ethToUsd } = useEthToUsdValue();

  const fees = breakdown?.fee || 0;
  const totalPrice = breakdown?.total || 0;
  const editionPriceEther = Number(totalPriceEth);
  const editionPriceUsd = editionPriceEther * Number(ethToUsd || 0);

  const clientId = React.useMemo(
    () =>
      (() => {
        switch (contractInterFaceId) {
          case ALLOWED_INTERFACES_FOR_CC.IRangeEditionMinter:
            return getCrossMintContractClientIds(release.chainId).minterAdapter;
          case ALLOWED_INTERFACES_FOR_CC.IMerkleDropMinterV2:
            return getCrossMintContractClientIds(release.chainId).merkleDropMinterV2;
          case ALLOWED_INTERFACES_FOR_CC.IRangeEditionMinterV2:
            return getCrossMintContractClientIds(release.chainId).rangeEditionMinterV2;
          case ALLOWED_INTERFACES_FOR_CC.IMerkleDropMinterV2_1:
            return getCrossMintContractClientIds(release.chainId).merkleDropMinterV2_1;
          case ALLOWED_INTERFACES_FOR_CC.IRangeEditionMinterV2_1:
            return getCrossMintContractClientIds(release.chainId).rangeEditionMinterV2_1;
          default:
            logError({
              level: 'fatal',
              errorType: 'CROSSMINT_CC',
              action: 'CROSSMINT_ERROR',
              isArtistOnlyPage: false,
              error: new Error("CrossMint client id doesn't exist"),
              message: 'Unable to find CrossMint client id for contract interface',
              pillar: 'MINTING',
              indexedTags: {
                contractInterFaceId,
                contractAddress,
                releaseId: release.id,
                chainId: release.chainId,
                quantity,
              },
            });
            return null;
        }
      })(),
    [contractInterFaceId, logError, quantity, release.chainId, release.id, contractAddress],
  );

  const mintConfig = React.useMemo(
    () =>
      (() => {
        const edition = contractAddress;

        switch (contractInterFaceId) {
          case ALLOWED_INTERFACES_FOR_CC.IRangeEditionMinter:
            return {
              type: 'erc-721',
              totalPrice: totalPriceEth,
              minter: minterAddress,
              edition,
              mintId,
              quantity,
              affiliate,
              attributionId: CROSSMINT_CC_ATTRIBUTION_ID,
            };
          case ALLOWED_INTERFACES_FOR_CC.IRangeEditionMinterV2_1:
          case ALLOWED_INTERFACES_FOR_CC.IRangeEditionMinterV2:
            return {
              type: 'erc-721',
              totalPrice: totalPriceEth,
              edition,
              mintId,
              quantity,
              affiliate,
              affiliateProof: [],
              attributionId: CROSSMINT_CC_ATTRIBUTION_ID,
            };
          case ALLOWED_INTERFACES_FOR_CC.IMerkleDropMinterV2:
          case ALLOWED_INTERFACES_FOR_CC.IMerkleDropMinterV2_1:
            if (!authUserAddress) return null;

            if (proof?.length === 0) {
              logError({
                level: 'fatal',
                errorType: 'CROSSMINT_CC',
                action: 'CROSSMINT_ERROR',
                isArtistOnlyPage: false,
                error: new Error('Unable to find user in the merkle tree'),
                message: 'Unable to find user in the merkle tree',
                pillar: 'MINTING',
                indexedTags: {
                  contractInterFaceId,
                  releaseId: release.id,
                  contractAddress,
                  chainId: release.chainId,
                  quantity,
                },
              });
              return null;
            }

            return {
              type: 'erc-721',
              totalPrice: totalPriceEth,
              edition,
              mintId,
              quantity,
              allowlisted: authUserAddress,
              proof,
              affiliate,
              affiliateProof: [],
              attributionId: CROSSMINT_CC_ATTRIBUTION_ID,
            };
          default:
            logError({
              level: 'fatal',
              errorType: 'CROSSMINT_CC',
              action: 'CROSSMINT_ERROR',
              isArtistOnlyPage: false,
              error: new Error("Unsupported contract interface for CrossMint's CC"),
              message: "Unsupported contract interface for CrossMint's CC",
              pillar: 'MINTING',
              indexedTags: {
                contractInterFaceId,
                releaseId: release.id,
                contractAddress,
                chainId: release.chainId,
                quantity,
              },
            });
            return null;
        }
      })(),
    [
      affiliate,
      authUserAddress,
      totalPriceEth,
      contractAddress,
      contractInterFaceId,
      logError,
      mintId,
      minterAddress,
      proof,
      quantity,
      release.chainId,
      release.id,
    ],
  );

  if (!clientId) return null;
  if (!mintConfig) return null;

  return (
    <StandardModal
      bodyWithoutPaddingBottom
      body={
        <>
          <SongDetails data={data} selectedTier={null} />
          {breakdown && (
            <div className="my-6 flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <p className="text-base-m font-medium text-base800">Edition x {quantity}</p>
                <p className="text-base-m text-base600">${editionPriceUsd.toFixed(2)}</p>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-base-m font-medium text-base800">
                  <span>Processing Fees</span>
                  <Tooltip openOnClick={!tabletLandscape}>
                    <Tooltip.Trigger>
                      <FontAwesomeIcon
                        icon={faQuestionCircle}
                        fontSize={12}
                        className="ml-1 text-base500"
                      />
                    </Tooltip.Trigger>
                    <Tooltip.StyledContent collisionPadding={{ left: 10 }}>
                      <p className="max-w-xs text-base-s ">{CREDIT_CARD_FEE_INFO_COPY}</p>
                    </Tooltip.StyledContent>
                  </Tooltip>
                </p>
                <p className="text-base-m text-base600">${fees.toFixed(2)}</p>
              </div>
              <div className="flex items-center justify-between font-title text-title-s font-medium text-base800">
                <p>Total Price</p>
                <p>${totalPrice.toFixed(2)}</p>
              </div>
            </div>
          )}
          <div className="[&>iframe]:w-full">
            <CrossmintPaymentElement
              currency="usd"
              clientId={clientId}
              onEvent={event => {
                switch (event.type) {
                  case 'quote:status.changed': {
                    // We have currency as USD, so crossmint gives us the total in USD
                    const totalUsd = Number(event.payload?.totalPrice?.amount || 0);
                    /**
                     * Due to legal reasons crossmint cannot provide us breakdown of their fee.
                     * It is included in the total price of NFT.
                     *
                     * We know how much edition cost, meaning how much we want them to charge for NFT.
                     * So we subtract edition price from total price to get fee.
                     */
                    const fee = totalUsd - editionPriceUsd;

                    setBreakdown({
                      total: totalUsd,
                      fee,
                    });
                    break;
                  }
                  case 'payment:process.succeeded': {
                    onSuccessfulTransaction();
                    openModal(ModalType.CROSSMINT_PROCESSING, {
                      body: (
                        <CrossMintProcessingModal
                          orderIdentifier={event.payload.orderIdentifier}
                          releaseId={release.id}
                          chainId={release.chainId}
                        />
                      ),
                    });
                    break;
                  }
                  case 'payment:process.canceled':
                  case 'payment:process.rejected':
                  case 'payment:preparation.failed':
                  case 'transaction:fulfillment.failed': {
                    logError({
                      toast: 'Unable to prepare credit card payment. Please contact support.',
                      level: 'error',
                      errorType: 'CROSSMINT_CC',
                      action: 'CROSSMINT_ERROR',
                      isArtistOnlyPage: false,
                      error: new Error('CrossMint payment preparation failed'),
                      message: 'CrossMint failed to prepare payment',
                      pillar: 'MINTING',
                      indexedTags: {
                        chainId: release.chainId,
                        releaseId: release.id,
                        contractAddress,
                        clientId,
                      },
                      unindexedExtra: {
                        event,
                      },
                    });
                    closeCancelModal();
                    break;
                  }
                  default: {
                    break;
                  }
                }
              }}
              mintConfig={mintConfig}
              locale="en-US"
              recipient={{
                wallet: authUserAddress,
              }}
              emailInputOptions={{
                show: true,
                useStripeLink: true,
              }}
              environment={CROSSMINT_ENVIRONMENT}
            />
          </div>
        </>
      }
      title="Pay with Card"
      closeModal={closeCancelModal}
    />
  );
};

function getSamContractId({ samAddress, chainId }: { samAddress: string; chainId: number }) {
  const chain = getChainName(chainId);
  const contractAddress = samAddress.toLowerCase();
  const { soundSwap, soundSwapV1_1 } = getCrossMintContractClientIds(chainId);

  if (contractAddress === getDeprecateMintersForChain(chain).sam.toLowerCase()) {
    return soundSwap;
  } else if (contractAddress === getContractsForChain(chain).samV1_1.toLowerCase()) {
    return soundSwapV1_1;
  } else {
    throw new Error('CrossMint contract not found');
  }
}

export const CrossMintSamCreditCardModal = ({
  buyPriceWei,
  quantity,
  data,
  contractAddress,
  affiliate,
  onSuccessfulTransaction,
}: {
  buyPriceWei: string;
  quantity: number;
  contractAddress: string;
  affiliate: string;
  data: FragmentType<BuyEditionSongDetailsFragmentDoc>;
  onSuccessfulTransaction: () => void;
}) => {
  const { authUserAddress } = useAuth();
  const { closeCancelModal, openModal } = ModalContext.useContainer();
  const logError = useLogError();
  const [breakdown, setBreakdown] = React.useState<{ total: number; fee: number }>();
  const release = getFragment(BuyEditionSongDetailsFragmentDoc, data);
  const { tabletLandscape } = MediaQuery.useContainer();
  const { ethToUsd } = useEthToUsdValue();

  if (!release.samAddress) {
    logError({
      level: 'fatal',
      action: 'SAM_MINT_EDITION_ERROR',
      errorType: 'CROSSMINT_CC',
      error: new Error('No SAM Address found for release'),
      message: 'No SAM Address found for release',
      indexedTags: {
        releaseId: release.id,
        contractAddress,
        chainId: release.chainId,
      },
      pillar: 'MINTING',
      isArtistOnlyPage: false,
    });
    return null;
  }

  const clientId = getSamContractId({ samAddress: release.samAddress, chainId: release.chainId });

  const fees = breakdown?.fee || 0;
  const totalPrice = breakdown?.total || 0;
  const editionPriceEther = Number(formatWeiToEthForDisplay({ weiValue: buyPriceWei }));
  const editionPriceUsd = editionPriceEther * Number(ethToUsd || 0);

  return (
    <StandardModal
      bodyWithoutPaddingBottom
      body={
        <>
          <SongDetails data={data} selectedTier={null} />
          {breakdown && (
            <div className="my-6 flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <p className="text-base-m font-medium text-base800">Edition x {quantity}</p>
                <p className="text-base-m text-base600">${editionPriceUsd.toFixed(2)}</p>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-base-m font-medium text-base800">
                  <span>Processing Fees</span>
                  <Tooltip openOnClick={!tabletLandscape}>
                    <Tooltip.Trigger>
                      <FontAwesomeIcon
                        icon={faQuestionCircle}
                        fontSize={12}
                        className="ml-1 text-base500"
                      />
                    </Tooltip.Trigger>
                    <Tooltip.StyledContent collisionPadding={{ left: 10 }}>
                      <p className="max-w-xs text-base-s ">{CREDIT_CARD_FEE_INFO_COPY}</p>
                    </Tooltip.StyledContent>
                  </Tooltip>
                </p>
                <p className="text-base-m text-base600">${fees.toFixed(2)}</p>
              </div>
              <div className="flex items-center justify-between font-title text-title-s font-medium text-base800">
                <p>Total Price</p>
                <p>${totalPrice.toFixed(2)}</p>
              </div>
            </div>
          )}
          <div className="[&>iframe]:w-full">
            <CrossmintPaymentElement
              currency="usd"
              clientId={clientId}
              onEvent={event => {
                switch (event.type) {
                  case 'quote:status.changed': {
                    // We have currency as USD, so crossmint gives us the total in USD
                    const totalUsd = Number(event.payload?.totalPrice?.amount || 0);
                    /**
                     * Due to legal reasons crossmint cannot provide us breakdown of their fee.
                     * It is included in the total price of NFT.
                     *
                     * We know how much edition cost, meaning how much we want them to charge for NFT.
                     * So we subtract edition price from total price to get fee.
                     */
                    const fee = totalUsd - editionPriceUsd;

                    setBreakdown({
                      total: totalUsd,
                      fee,
                    });
                    break;
                  }
                  case 'payment:process.succeeded': {
                    onSuccessfulTransaction();
                    openModal(ModalType.CROSSMINT_PROCESSING, {
                      body: (
                        <CrossMintProcessingModal
                          orderIdentifier={event.payload.orderIdentifier}
                          releaseId={release.id}
                          chainId={release.chainId}
                        />
                      ),
                    });
                    break;
                  }
                  case 'payment:process.canceled':
                  case 'payment:process.rejected':
                  case 'payment:preparation.failed':
                  case 'transaction:fulfillment.failed': {
                    logError({
                      toast: 'Unable to prepare credit card payment. Please contact support.',
                      level: 'error',
                      errorType: 'CROSSMINT_CC',
                      action: 'CROSSMINT_ERROR',
                      isArtistOnlyPage: false,
                      error: new Error('CrossMint payment preparation failed'),
                      message: 'CrossMint failed to prepare payment',
                      pillar: 'MINTING',
                      indexedTags: {
                        chainId: release.chainId,
                        releaseId: release.id,
                        contractAddress,
                        clientId,
                      },
                      unindexedExtra: {
                        event,
                      },
                    });
                    closeCancelModal();
                    break;
                  }
                  default: {
                    break;
                  }
                }
              }}
              mintConfig={{
                type: 'erc-721',
                totalPrice: formatEther(BigInt(buyPriceWei)),
                edition: contractAddress,
                quantity,
                affiliate,
                affiliateProof: [],
                attributonId: CROSSMINT_CC_ATTRIBUTION_ID,
              }}
              locale="en-US"
              recipient={{
                wallet: authUserAddress,
              }}
              emailInputOptions={{
                show: true,
                useStripeLink: true,
              }}
              environment={CROSSMINT_ENVIRONMENT}
            />
          </div>
        </>
      }
      title="Pay with Card"
      closeModal={closeCancelModal}
      mobileFullScreen
      containerClassName="pb-0"
    />
  );
};
